import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Comment fonctionne un volet roulant solaire ?', a: 'Un volet roulant solaire est équipé d\'un petit panneau photovoltaïque (généralement fixé sur le coffre) qui recharge une batterie intégrée. Cette batterie alimente le moteur. C\'est un système autonome, sans raccordement électrique, idéal pour les fenêtres éloignées d\'une prise électrique.' },
  { q: 'La batterie de mon volet solaire ne tient plus la charge, que faire ?', a: 'C\'est la panne la plus fréquente sur les volets solaires. La batterie a une durée de vie limitée (environ 5 à 8 ans). Nous diagnostiquons l\'état de la batterie et du panneau solaire, et remplaçons le ou les composants défectueux.' },
];

export default function VoletSolaire() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Volet roulant solaire à Lyon : pose & dépannage | DFPRO"
        description="Panne batterie, panneau solaire ou moteur de volet roulant solaire à Lyon ? Dépannage et installation par technicien qualifié. Devis gratuit. 09 80 80 36 84."
        canonicalUrl="https://depanfenetres-lyon.fr/volet-roulant-solaire-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-solaire-2026.webp" alt="Dépannage et pose de volet roulant solaire à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Volet solaire</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Volet roulant solaire à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Votre volet roulant solaire ne fonctionne plus ? Batterie HS, panneau défectueux ? Nos techniciens diagnostiquent et réparent votre volet solaire à Lyon.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Le volet roulant solaire : une solution autonome et écologique</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le volet roulant solaire est une innovation qui séduit de plus en plus de Lyonnais. Grâce à un petit panneau photovoltaïque intégré au coffre du volet, il fonctionne de manière totalement autonome, sans aucun raccordement électrique. C'est la solution idéale pour les fenêtres difficiles à câbler ou pour ceux qui souhaitent réduire leur consommation électrique.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le système est simple : le panneau solaire capte la lumière du jour (même par temps couvert, ce qui est pratique à Lyon !) et recharge une batterie interne. Cette batterie alimente le moteur lors des cycles d'ouverture et de fermeture. Un volet solaire bien entretenu peut fonctionner de nombreuses années sans intervention.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Cependant, comme tout équipement, le volet solaire peut connaître des pannes. La plus fréquente est la défaillance de la batterie, qui perd sa capacité de charge après 5 à 8 ans. Le panneau solaire lui-même peut aussi se dégrader (microfissures, encrassement), et le moteur peut tomber en panne comme sur n'importe quel volet électrique.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Pannes courantes des volets roulants solaires</h2>
                  <div className="mt-4 space-y-3">
                    <div className="bg-background-100 rounded-lg p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Batterie HS</h3>
                      <p className="mt-1 text-sm text-secondary-500">Le volet ne réagit plus, ou seulement quelques secondes avant de s'arrêter. La batterie ne tient plus la charge. Remplacement par une batterie neuve compatible.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Panneau solaire défectueux</h3>
                      <p className="mt-1 text-sm text-secondary-500">La batterie ne se recharge plus assez. Nous vérifions la tension du panneau et le remplaçons si nécessaire.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Moteur en panne</h3>
                      <p className="mt-1 text-sm text-secondary-500">Même problème que sur un volet électrique classique. Nous remplaçons le moteur par un modèle solaire compatible.</p>
                    </div>
                  </div>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/volet-roulant-electrique-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet électrique</Link>
                    <Link to="/reparation-moteur-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation moteur</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — volet roulant solaire</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Volet solaire en panne à Lyon ?" subtitle="Diagnostic gratuit de votre batterie et panneau solaire. Intervention rapide." />
      </main>
      <Footer />
    </>
  );
}